import { useAddFinancialDocumentsToImporterMutation } from "@api/financialDocument.api";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { FinancialDocumentSelect } from "@components/documents/FinancialDocumentSelect/FinancialDocumentSelect";
import { DocumentSelectVariant } from "@components/documents/FinancialDocumentSelect/financialDocumentSelectViewConfig";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralizeWithCount } from "@common/helpers/string";
import { useCallback, useContext, useMemo, useState } from "react";
import { ImporterContext } from "./importer.context";
import { ImporterPickExistingDocumentCta } from "./ImporterPickExistingDocumentCta";
import { getImporterDocumentEligibility } from "./importerDocumentEligibility";
import { useNotifyUnfinishedImport } from "./useNotifyUnfinishedImport";

export const ImporterPickExistingDocument: React.FC = () => {
    const { importerQueue, reconciliedFinancialAccount } =
        useContext(ImporterContext);
    const [documents, setDocuments] = useState<FinancialDocumentListItem[]>([]);
    const toaster = useToaster();
    const notifyUnfinishedImport = useNotifyUnfinishedImport("transactions");

    const nonSelectableDocumentIds = useMemo(
        () =>
            documents
                .filter(
                    (financialDocument) =>
                        getImporterDocumentEligibility({
                            financialDocument,
                            importerQueue,
                        }).isDisabled,
                )
                .map((financialDocument) => financialDocument.id),
        [documents, importerQueue],
    );

    const addDocuments = useAddFinancialDocumentsToImporterMutation({
        importerQueue: reconciliedFinancialAccount ? undefined : importerQueue,
        reconciliedFinancialAccountId: reconciliedFinancialAccount?.id,
        onSettled: ({ updatedDocuments, failedCount }) => {
            if (failedCount > 0) {
                toaster.error(
                    `Failed to add ${pluralizeWithCount("document", failedCount)} to the importer`,
                );
            }
            notifyUnfinishedImport(updatedDocuments);
        },
    });

    const attachDocuments = useCallback(
        (documentIds: number[]) => {
            const selectedDocuments = documents.filter((financialDocument) =>
                documentIds.includes(financialDocument.id),
            );
            if (selectedDocuments.length === 0) {
                return;
            }
            addDocuments.mutate(selectedDocuments);
        },
        [documents, addDocuments],
    );

    const getRowSelectionDisabledTooltip = useCallback(
        (financialDocument: FinancialDocumentListItem) =>
            getImporterDocumentEligibility({
                financialDocument,
                importerQueue,
            }).disabledTooltip,
        [importerQueue],
    );

    return (
        <FinancialDocumentSelect
            variant={DocumentSelectVariant.IMPORTER}
            cta={(financialDocument) => (
                <ImporterPickExistingDocumentCta
                    financialDocument={financialDocument}
                />
            )}
            attachedDocumentIds={nonSelectableDocumentIds}
            attachDocuments={attachDocuments}
            getRowSelectionDisabledTooltip={getRowSelectionDisabledTooltip}
            onDocumentsChanged={setDocuments}
        />
    );
};
